import { UIElement } from '../ui-element';
import { UINavList } from './ui-navlist';
import { UINavToggle } from './ui-navtoggle';
import { eventBus } from '../../global/event-bus';
import { EventObserver } from '../../global/event-observer';
import { runInNextFrame } from '../../global/helpers';
import {
    closestEventHolder,
    insertElements,
    queryChildren,
    setAttributes,
    updateClassList,
    isVisible,
    addOutsideClickHandler,
} from '../../global/ui-helpers';
import { isEscapePressed, TabIndex } from '../../global/keyboard';
import { ComponentState, INERT_MEDIA_QUERY_LIST } from '../../global/enums';
import { prerenderElementsTree } from '../../global/render-api';
import { Labels } from '../../global/labels';
import { focusHandler } from '../../global/focus-handler';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type {
    AttributeValue,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    ClassModifiers,
    NullOr,
    UndefinedOr,
} from '../../global/utils.type';
import type {
    UINavigationConfig,
    MenuItem,
    UIBreadCrumbsItem,
} from './menu.type';
import type {
    DeclarativeEventDetail,
    MenuSearchToggleDetail,
    MenuToggleDetail,
    NavigationChangeDetail,
    UINavLayout,
    UINavLayouts,
} from './ui-nav.type';
import type { UINavItem } from './ui-navitem';
import type { UIBreadCrumbs } from './ui-breadcrumbs';
import type { UINavSearch } from './ui-navsearch';
import { browser } from '../../global/browser';
import styles from './ui-nav.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UINav
 * @element ui-nav
 * @classdesc Represents a class for <code>ui-nav</code> element
 * @fires event:menu-toggle
 * @fires event:menu-open
 * @fires event:menu-close
 * @fires event:change
 * @listens event:spa-navigation-change
 * @listens event:menu-toggle
 * @listens event:menu-search-toggle
 * @listens event:menu-back
 * @listens event:open-shortcuts
 * @listens event:edit-shortcuts
 * @listens click
 * @listens window.onpopstate
 * @property {INavigationConfig} config navigation config
 * @property {Function} buildLinkHandler function to build link handler
 * @property {boolean | attr} business - indicate if view is business, (affects breadcrumbs for eg)
 * @property {UIBreadCrumbs} breadcrumbs
 * @property {HTMLDivElement} primaryColumns
 * @property {HTMLDivElement} secondaryColumns
 * @property {HTMLDivElement} auxiliaryColumns
 * @property {HTMLDivElement} wrapper
 * @property {NodeList<UINavItem>} primaryItems
 * @property {NodeList<UINavItem>} primaryIcons
 * @property {string | attr} label label for menu toggle button
 * @property {string | attr} labelSearch label for search input
 * @property {string | attr} labelSearchopen label for search open button
 * @property {string | attr} labelSearchclose label for search close button
 * @property {string | attr} homeId home id
 * @property {string | attr} useSearch indicates if search is used
 * @property {boolean | attr} simpleSearch - indicates if simple search is used
 * @property {boolean | attr} spa - indicates if menu is rendered in SPA.
 * @property {string | attr} searchHomeId search home id
 * @property {string | attr} useHome indicates if home is used
 * @property {string | attr} useBreadcrumbs indicates if breadcrumbs are used
 * @property {string | attr} useShortcuts indicates if shortcuts are used
 * @property {"popover" | attr} layout - layout for ex., ui-nav inside ui-navtoggle's popover
 * @example
 * <ui-nav></ui-nav>
 */
@Component({ tag: 'ui-nav', styles })
export class UINav extends UIElement {
    static readonly ModifierClasses: ClassModifiers = {
        On: '-on',
        Off: '-off',
        Expanded: '-expanded',
        Previous: '-previous',
        Next: '-next',
        Primary: '-primary',
    } as const;

    @Attribute(Boolean) open?: boolean;
    @Attribute(Boolean) dropdown?: boolean;
    @Attribute(Boolean) business?: boolean;
    @Attribute(String) homeId?: string;
    @Attribute(String) useSearch?: string;
    @Attribute(Boolean) simpleSearch?: boolean;
    @Attribute(Boolean) spa?: boolean;
    @Attribute(String) searchHomeId?: string;
    @Attribute(String) useHome?: string;
    @Attribute(String) useBreadcrumbs?: string;
    @Attribute(String) useShortcuts?: string;
    @Attribute(String) label?: string;
    @Attribute(String) labelSearch?: string;
    @Attribute(String) labelSearchopen?: string;
    @Attribute(String) labelSearchclose?: string;
    @Attribute(String) layout?: UINavLayout | string;

    @Child('ui-breadcrumbs') breadcrumbs?: UIBreadCrumbs;
    @Child('.ui-nav__columns-wrapper') wrapper?: HTMLDivElement;
    @Child('.ui-nav__columns.-primary') primaryColumns!: HTMLDivElement;
    @Child('.ui-nav__columns.-secondary') secondaryColumns!: HTMLDivElement;
    @Child('.ui-nav__columns.-auxiliary') auxiliaryColumns!: HTMLDivElement;
    @Children('ui-navitem[type="primary-icon"]')
    primaryIcons!: Array<UINavItem>;
    @Children('ui-navitem[type="primary-item"]')
    primaryItems!: Array<UINavItem>;

    /** Navigation config set via init() */
    config?: UINavigationConfig;
    /** @private */
    buildLinkHandler?: (id: string) => string;
    /** @private */
    buildIdHandler?: (url: string) => string;
    /** @private */
    currentIdHandler?: () => string;
    /** @private */
    private outsideClickRemover?: () => void;
    /** @private */
    private readonly clickObserver = new EventObserver({ name: 'click' });
    /** @private */
    private previouslyActivePrimaryButton?: NullOr<HTMLElement>;
    private readonly desktopMedia = browser.run(
        () => window.matchMedia('(min-width: 1024px)'),
        INERT_MEDIA_QUERY_LIST
    );
    private readonly mobileMedia = browser.run(
        () => window.matchMedia('(max-width: 1023.98px)'),
        INERT_MEDIA_QUERY_LIST
    );
    private outsideClickHandlerRequestId = 0;
    private readonly clickInsideHandler = this.handleClickInside.bind(this);
    private readonly navigationChangeHandler: EventListener = (event) => {
        this.handleNavigationChange(
            event as CustomEvent<NavigationChangeDetail>
        );
    };
    private readonly mobileMediaChangesHandler =
        this.handleMobileMediaChanges.bind(this);
    private readonly escapeHandler = this.handleEscape.bind(this);
    private readonly toggleMenuHandler: EventListener = (event) => {
        this.handleToggleMenu(event as CustomEvent<MenuToggleDetail>);
    };
    private readonly toggleSearchHandler: EventListener = (event) => {
        this.handleToggleSearch(event as CustomEvent<MenuSearchToggleDetail>);
    };
    private readonly backMenuHandler = this.handleBackMenu.bind(this);
    private readonly navtoggleCloseHandler =
        this.handleNavtoggleClose.bind(this);
    private readonly browserHistoryStateHandler =
        this.handleBrowserHistoryState.bind(this);
    private readonly openShortcutsHandler: EventListener = (event) => {
        this.handleOpenShortcuts(
            event as CustomEvent<DeclarativeEventDetail<UINavItem>>
        );
    };
    private readonly editShortcutsHandler: EventListener = (event) => {
        this.handleEditShortcuts(event as CustomEvent<DeclarativeEventDetail>);
    };
    private readonly documentClicksHandler = (event?: Event): void => {
        if (event) {
            this.handleDocumentClicks(event);
        }
    };

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'open',
            'dropdown',
            'label-search',
            'label',
            'business',
            'label-searchopen',
            'label-searchclose',
            'use-breadcrumbs',
        ];
    }

    static readonly Layout: UINavLayouts = {
        Popover: 'popover',
    };

    /**
     * @type {Labels}
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-nav', {
            search: 'Search',
            searchOpen: 'Open search form',
            searchClose: 'Close search form',
        });
    }

    get desktopMediaQueryList(): MediaQueryList {
        return this.desktopMedia;
    }

    get mobileMediaQueryList(): MediaQueryList {
        return this.mobileMedia;
    }

    /**
     * @returns {boolean}
     */
    isSecondaryMenuOpen(): boolean {
        return this.secondaryColumns.classList.contains(
            UINav.ModifierClasses.On
        );
    }

    /**
     * @returns {boolean}
     */
    isAuxiliaryMenuOpen(): boolean {
        return this.auxiliaryColumns.classList.contains(
            UINav.ModifierClasses.On
        );
    }

    /**
     * @returns {boolean}
     */
    isDesktopMode(): boolean {
        return this.desktopMediaQueryList.matches;
    }

    /**
     * @returns {boolean}
     */
    isMobileMode(): boolean {
        return this.mobileMediaQueryList.matches;
    }

    /**
     * @returns {boolean}
     */
    isActiveDesktopMode(): boolean {
        return this.isDesktopMode() && !!this.dropdown;
    }

    /**
     * @returns {boolean}
     */
    isActiveMobileMode(): boolean {
        return this.isMobileMode() && !!this.open;
    }

    /**
     * @returns {boolean}
     */
    isSearchBarOpen(): boolean {
        const navItems = this.querySelector<HTMLElement>('.ui-nav__items');
        return navItems?.classList.contains('-search-open') || false;
    }

    rebuildShortcuts(shortcuts: Array<MenuItem>): this {
        const list = this.querySelector<UINavList>(
            'ui-navlist[ref="shortcuts"]'
        );
        const editMode = list?.edit;

        if (editMode) {
            list.toggleEditMode?.();
        }

        this.rebuildChildren.call(
            list?.querySelector('.ui-navlist-menu__items'),
            shortcuts.map((shortcut) => {
                return {
                    tagName: 'ui-navitem',
                    attributes: {
                        'type': 'secondary-item',
                        'label': shortcut.label || shortcut.labelENG,
                        'label-id': shortcut.labelENG,
                        'item': shortcut.id,
                        'href': this.buildLink(shortcut.id),
                        'edit': list?.edit,
                    },
                    children: [],
                };
            })
        );

        if (editMode) {
            list.toggleEditMode?.();
        }

        return this;
    }

    updateShortcuts(pageId: string): void {
        /** @type {UINavItem} */
        const shortcutControl = this.querySelector<UINavItem>(
            'ui-navitem[controls="shortcuts-nav-container"]'
        );
        if (!shortcutControl) {
            return;
        }
        const hasShortcut =
            (this.config?.shortcutsList || []).filter(
                (item) => item.id === pageId
            ).length > 0;

        shortcutControl.updateElement({
            classList: { '-selected': hasShortcut },
            attributes: { 'aria-current': hasShortcut ? 'true' : null },
        });

        const shortcutMenu = this.querySelector<UINavList>(
            'ui-navlist[ref="shortcuts"]'
        );
        shortcutMenu?.setActiveShortcut(pageId);
    }

    /**
     * @param { MenuItem } item
     * @returns { boolean }
     */
    isPrimaryItemExpandable(item: MenuItem): boolean {
        return (
            (item.children || []).filter((child: MenuItem) => {
                if (!child.visible || !child.children) {
                    return false;
                }
                return (
                    child.children.filter((grandChild: MenuItem) => {
                        return grandChild.visible;
                    }).length > 0
                );
            }).length > 0
        );
    }

    /**
     * @param { MenuItem } item
     * @returns { boolean }
     */
    isSecondaryItemExpandable(item: MenuItem): boolean {
        return (
            (item.children || []).filter((child: MenuItem) => {
                return child.visible;
            }).length > 0
        );
    }

    /**
     * Fires callback when user clicked
     * @param {string} url
     * @private
     */
    private selectMenuItem(url: string): void {
        this.selectMenuItemById(this.buildId(url));
    }

    /**
     * Fires callback when user clicked
     * @param {string} pageId
     * @private
     */
    selectMenuItemById(pageId: string): void {
        const newBreadcrumbs: Array<UIBreadCrumbsItem> = [];

        if (!this.isMobileMode()) {
            this.updateAuxiliaryMenu(pageId);
        }

        if (!pageId) {
            this.updateShortcuts(pageId);
            if (this.useBreadcrumbs !== ComponentState.Off) {
                this.breadcrumbs?.setItems([]);
            }
            return;
        }

        const navItemSelector =
            'ui-navitem:not([controls="shortcuts-nav-container"])';
        this.querySelectorAll<UINavItem>(
            navItemSelector + '.-selected'
        ).forEach((item) => item.deactivate());

        if (pageId === this.homeId) {
            this.updateShortcuts(pageId);
            if (this.useBreadcrumbs !== ComponentState.Off) {
                this.breadcrumbs?.setItems([]);
            }
            const homeItem = this.querySelector<UINavItem>(
                navItemSelector + '[item="' + pageId + '"]'
            );
            if (homeItem) {
                homeItem.activate();
                return;
            }
        }

        if (this.simpleSearch && pageId === this.searchHomeId) {
            const searchItem = this.querySelector<UINavItem>(
                navItemSelector + '[item="search"]'
            );
            if (searchItem) {
                // For some reason the menu rendered in Angular doesn't reset
                // the breadcrumbs when clicked the search link.
                if (this.useBreadcrumbs !== ComponentState.Off) {
                    this.breadcrumbs?.setItems([]);
                }

                searchItem.activate();
                return;
            }
        }

        const ids = pageId.split('.');

        const lvl1 = ids.length > 1 ? [ids[0], ids[1]].join('.') : null;
        const lvl1Item = lvl1
            ? this.querySelector<UINavItem>(
                  navItemSelector + '[item="' + lvl1 + '"]'
              )
            : null;

        const lvl4 =
            ids.length > 3 ? [ids[0], ids[1], ids[2], ids[3]].join('.') : null;
        const lvl4Item =
            lvl4 && lvl1Item
                ? lvl1Item
                      .getControlList?.()
                      ?.querySelector<UINavItem>(
                          'ui-navitem[item="' + lvl4 + '"]'
                      )
                : null;

        const lvl2Item = lvl4Item ? lvl4Item.closest('ui-navlist-menu') : null;

        const lvl5 =
            ids.length > 4
                ? [ids[0], ids[1], ids[2], ids[3], ids[4]].join('.')
                : null;
        const lvl5Item = lvl5
            ? this.auxiliaryColumns.querySelector<UINavItem>(
                  'ui-navitem[item="' + lvl5 + '"]'
              )
            : null;

        if (this.homeId && this.label) {
            newBreadcrumbs.push({
                label: this.label,
                href: this.buildLink(this.homeId),
            });
        }

        if (lvl1Item) {
            lvl1Item.activate();
            newBreadcrumbs.push({
                label: lvl1Item.getAttribute('label')!,
            });
        }

        if (lvl2Item) {
            newBreadcrumbs.push({
                label: lvl2Item.getAttribute('label')!,
            });
        }

        if (lvl4Item) {
            lvl4Item.activate();
            newBreadcrumbs.push({
                label: lvl4Item.getAttribute('label')!,
                href: lvl5Item ? this.buildLink(lvl4!) : null!,
            });
        }

        if (lvl5Item) {
            lvl5Item.activate();
            newBreadcrumbs.push({
                label: lvl5Item.getAttribute('label')!,
            });
        }

        this.updateShortcuts(pageId);
        if (this.useBreadcrumbs !== ComponentState.Off) {
            this.breadcrumbs?.setItems(newBreadcrumbs);
            this.spa && this.addSpaBreadcrumbsNavigationEventAdapter();
        }
    }

    /**
     * Build link for given id
     * @param { string } itemId
     * @returns { string }
     */
    buildLink(itemId: string): string {
        if (typeof this.buildLinkHandler !== 'function') {
            return itemId;
        }
        return this.buildLinkHandler(itemId);
    }

    /**
     * Build id for given link
     * @param { string } url
     * @returns { string }
     */
    buildId(url: string): string {
        if (typeof this.buildIdHandler !== 'function') {
            return url;
        }
        return this.buildIdHandler(url);
    }

    /**
     * Get id of current page
     * @returns { string }
     */
    currentPageId(): string {
        if (typeof this.currentIdHandler !== 'function') {
            return '';
        }
        return this.currentIdHandler();
    }

    /**
     * @param { Function } buildLinkHandler
     * @returns { UINav }
     */
    setBuildLinkHandler(buildLinkHandler: (id: string) => string): this {
        if (typeof buildLinkHandler === 'function') {
            this.buildLinkHandler = buildLinkHandler;
        }
        return this;
    }

    /**
     * @param { Function } buildIdHandler
     * @returns { UINav }
     */
    setBuildIdHandler(buildIdHandler: (url: string) => string): this {
        if (typeof buildIdHandler === 'function') {
            this.buildIdHandler = buildIdHandler;
        }
        return this;
    }

    /**
     * @param { Function } currentIdHandler
     * @returns { UINav }
     */
    setCurrentIdHandler(currentIdHandler: () => string): this {
        if (typeof currentIdHandler === 'function') {
            this.currentIdHandler = currentIdHandler;
        }
        return this;
    }

    /**
     * @private
     * @param {NodeList | Array<HTMLAnchorElement>} navigationLinkElements
     */
    private addSpaNavigationEventAdapter(
        navigationLinkElements: NodeList | Array<HTMLAnchorElement>
    ): void {
        navigationLinkElements.forEach((navigationLinkElement) => {
            navigationLinkElement.addEventListener('click', (event) => {
                event.preventDefault();
                this.close();

                if (this.isMobileMode()) {
                    this.dispatchCustomEvent('menu-toggle', {
                        active: false,
                    });
                }

                this.dispatchCustomEvent('change', {
                    url:
                        (navigationLinkElement as HTMLAnchorElement)
                            .getAttribute('href')
                            ?.slice(1)
                            .replace(/\//g, '.') || '',
                    originalEvent: event,
                });
            });
        });
    }

    /**
     * @private
     */
    private addSpaBreadcrumbsNavigationEventAdapter(): void {
        const allBreadcrumbsLinkElements =
            this.breadcrumbs?.querySelectorAll<HTMLLinkElement>('a');
        if (allBreadcrumbsLinkElements) {
            this.addSpaNavigationEventAdapter(allBreadcrumbsLinkElements);
        }
    }

    /**
     * @private
     */
    private addSpaMenuNavigationEventAdapter(): void {
        const allLinkElements =
            this.wrapper?.querySelectorAll<HTMLLinkElement>('a');
        if (!allLinkElements) {
            return;
        }
        const navigationLinkElements = [].filter.call(
            allLinkElements,
            (link: HTMLAnchorElement) =>
                link.getAttribute('role') !== 'menuitem'
        );
        this.addSpaNavigationEventAdapter(navigationLinkElements);
    }

    /**
     * Initialize menu using given config
     * @param { UINavigationConfig } config
     * @returns { UINav }
     */
    init(config: UINavigationConfig): this {
        this.config = config;
        this.renderMenu(config);

        if (this.spa) {
            prerenderElementsTree(this, true);
            this.addSpaMenuNavigationEventAdapter();
        }

        return this;
    }

    /**
     * Set visibility for a search bar
     * @param {boolean} state
     */
    toggleSearchBar(state: boolean): void {
        const navItems = this.querySelector<HTMLElement>('.ui-nav__items');
        if (navItems) {
            updateClassList(navItems, {
                '-search-open': state,
            });
        }
    }

    /**
     * Open a secondary menu
     * @param {HTMLElement | null} element
     * @param {HTMLElement} menu
     */
    openSecondaryMenu(
        element: NullOr<HTMLElement>,
        menu?: NullOr<HTMLElement>
    ): void {
        if (this.isDesktopMode()) {
            this.dropdown = true;
        }

        updateClassList(this.primaryColumns, {
            [UINav.ModifierClasses.On]: false,
            [UINav.ModifierClasses.Off]: true,
        });

        updateClassList(this.secondaryColumns, {
            [UINav.ModifierClasses.On]: true,
            [UINav.ModifierClasses.Off]: false,
            [UINav.ModifierClasses.Expanded]: true,
            [UINav.ModifierClasses.Next]: !this.open,
        });

        updateClassList(this.auxiliaryColumns, {
            [UINav.ModifierClasses.On]: false,
            [UINav.ModifierClasses.Off]: false,
            [UINav.ModifierClasses.Next]: true,
        });

        // it closes previously opened menus
        this.closeSecondaryMenu();

        if (this.layout === UINav.Layout.Popover) {
            this.secondaryColumns.scrollTop = 0;
        }

        if (menu) {
            updateClassList(menu, {
                [UINav.ModifierClasses.Expanded]: true,
            });

            this.setAttributes.call(menu, {
                'aria-hidden': 'false',
            });
        }

        this.handleFocusMobileMenu(
            menu?.querySelectorAll<UINavItem>('ui-navitem') ?? []
        );

        // Activate focus trap on secondary menu
        focusHandler.createFocusTrap(this.secondaryColumns, this.escapeHandler);
    }

    /**
     * Focuses mobile menu navitem;
     * @param {NodeList} items
     */
    handleFocusMobileMenu(items: NodeListOf<Element> | Array<Element>): void {
        if (!this.isMobileMode()) {
            return;
        }
        const focusableItem = (
            [].find.call(items, (i: HTMLElement) => isVisible(i)) as
                | HTMLElement
                | undefined
        )?.firstElementChild as HTMLElement | undefined;

        const navListMenu = focusableItem?.closest('ui-navlist-menu') as
            | (HTMLElement & { tabIndex: number })
            | null;
        if (navListMenu) {
            navListMenu.tabIndex = Number(TabIndex.Active);
            navListMenu.focus();
        }
    }

    /**
     * Closes a secondary menu
     */
    closeSecondaryMenu(): void {
        const targetMenu = this.querySelector<UINavList>(
            'ui-navlist.-secondary.-expanded'
        );

        updateClassList(this.secondaryColumns, {
            [UINav.ModifierClasses.Next]:
                !!this.open && !this.isSecondaryMenuOpen(),
        });

        if (targetMenu) {
            updateClassList(targetMenu, {
                [UINav.ModifierClasses.Expanded]: false,
            });

            setAttributes(targetMenu, {
                'aria-hidden': 'true',
            });
        }

        if (!this.dropdown) {
            const activePrimary =
                this.querySelector<UINavItem>('ui-navitem.-active');
            activePrimary?.classList.remove('-active');
        }

        focusHandler.destroyFocusTrap(
            this.secondaryColumns,
            this.escapeHandler
        );
    }

    /**
     * Update auxiliary menu visibility
     * @param {string} itemId
     */
    updateAuxiliaryMenu(itemId: string): void {
        const ids = itemId ? itemId.split('.') : [];
        const lvl4 =
            ids.length > 3 ? [ids[0], ids[1], ids[2], ids[3]].join('.') : null;
        const auxiliaryMenu = lvl4
            ? this.auxiliaryColumns.querySelector<UINavList>(
                  'ui-navlist[ref="' + lvl4 + '"]'
              )
            : null;

        this.closeAuxiliaryMenu();

        if (auxiliaryMenu) {
            updateClassList(this.secondaryColumns, {
                [UINav.ModifierClasses.On]: false,
                [UINav.ModifierClasses.Off]: true,
                [UINav.ModifierClasses.Expanded]: false,
                [UINav.ModifierClasses.Next]: false,
            });

            updateClassList(this.auxiliaryColumns, {
                [UINav.ModifierClasses.On]: true,
                [UINav.ModifierClasses.Off]: false,
                [UINav.ModifierClasses.Next]: false,
            });

            auxiliaryMenu.activate();
            const mainLink = auxiliaryMenu.querySelector<UINavItem>(
                'ui-navitem.-frontpage-link'
            );
            if (mainLink) {
                mainLink.activate();
            }
            // If auxiliary menu is open, destroy focus trap for primary
            if (this.isMobileMode()) {
                focusHandler.destroyFocusTrap(
                    this.primaryColumns,
                    this.escapeHandler
                );
            }
        } else {
            updateClassList(this.primaryColumns, {
                [UINav.ModifierClasses.Off]: false,
                [UINav.ModifierClasses.On]: true,
            });
            // If auxiliary is closed but primary is open, re-activate focus trap on it
            if (this.isMobileMode() && this.open) {
                focusHandler.createFocusTrap(
                    this.primaryColumns,
                    this.escapeHandler
                );
            }
        }
    }

    /**
     * Closes an auxiliary menu
     */
    closeAuxiliaryMenu(): void {
        updateClassList(this.secondaryColumns, {
            [UINav.ModifierClasses.Off]: false,
        });

        updateClassList(this.auxiliaryColumns, {
            [UINav.ModifierClasses.On]: false,
            [UINav.ModifierClasses.Off]: false,
            [UINav.ModifierClasses.Next]: false,
        });

        this.auxiliaryColumns
            .querySelectorAll<UINavList>('ui-navlist.-expanded')
            .forEach((item) => item.deactivate());

        focusHandler.destroyFocusTrap(
            this.auxiliaryColumns,
            this.escapeHandler
        );
    }

    /**
     * Close menu if opened
     */
    close(): void {
        if (!this.isActiveDesktopMode() && !this.isActiveMobileMode()) {
            return;
        }

        this.dropdown = false;

        updateClassList(this.primaryColumns, {
            [UINav.ModifierClasses.Off]: false,
            [UINav.ModifierClasses.On]: true,
        });

        updateClassList(this.secondaryColumns, {
            [UINav.ModifierClasses.Off]: this.isActiveDesktopMode(),
            [UINav.ModifierClasses.On]: false,
            [UINav.ModifierClasses.Expanded]: false,
            [UINav.ModifierClasses.Next]: true,
        });

        updateClassList(this.auxiliaryColumns, {
            [UINav.ModifierClasses.Off]: false,
            [UINav.ModifierClasses.On]: false,
            [UINav.ModifierClasses.Next]: false,
        });

        if (this.isActiveMobileMode()) {
            this.closeAuxiliaryMenu();

            if (this.open) {
                // In case primary menu is still open, re-activate focus trap on it
                focusHandler.createFocusTrap(
                    this.primaryColumns,
                    this.escapeHandler
                );
            }
        }

        const activePrimary =
            this.querySelector<UINavItem>('ui-navitem.-active');
        if (activePrimary) {
            activePrimary.updateClassList?.({ '-active': false });
        }

        if (this.layout === UINav.Layout.Popover) {
            const content = this.closest<HTMLDivElement>(
                '.ui-navtoggle__content'
            );
            content?.removeAttribute('style');
            this.secondaryColumns.removeAttribute('style');
        }

        if (this.isDesktopMode()) {
            this.dispatchCustomEvent('menu-close', {});
            // In case primary menu is closed, destroy focus trap on it
            focusHandler.destroyFocusTrap(
                this.primaryColumns,
                this.escapeHandler
            );
        }
    }

    /**
     * @param {HTMLElement} navList
     */
    backFromList(navList: UINavList): void {
        if (navList.type === 'secondary') {
            this.close();
        } else if (navList.type === 'auxiliary') {
            const ids = (navList.getAttribute('ref') ?? '').split('.');
            const lvl2 = ids.length > 1 ? [ids[0], ids[1]].join('.') : null;
            const lvl2Item = lvl2
                ? this.secondaryColumns.querySelector<UINavList>(
                      'ui-navlist[ref="' + lvl2 + '"]'
                  )
                : null;
            this.openSecondaryMenu(null, lvl2Item);
        }
        // Focus previously active button if possible
        const previouslyActive = this.previouslyActivePrimaryButton;
        if (previouslyActive) {
            previouslyActive.focus();
            this.previouslyActivePrimaryButton = null;
        } else {
            // Focus first element in the menu as fallback
            (
                [...this.primaryIcons, ...this.primaryItems].find(isVisible)
                    ?.firstElementChild as HTMLElement | null
            )?.focus();
        }
    }

    /**
     * Fires callback when navigation is changed
     * @param {CustomEvent} event
     * @listens event:spa-navigation-change
     * @fires menu-toggle
     * @private
     */
    private handleNavigationChange(
        event: CustomEvent<NavigationChangeDetail>
    ): void {
        if (this.isActiveDesktopMode() || this.isActiveMobileMode()) {
            this.close();
        }
        this.selectMenuItem(event.detail.urlPath);
        if (this.isMobileMode()) {
            this.dispatchCustomEvent('menu-toggle', {
                active: false,
            });
        }
    }

    /**
     * Fires callback when user clicked outside of active nav menu
     * @param {CustomEvent | PointerEvent} event
     * @private
     */
    private handleDocumentClicks(event: Event): void {
        if (
            !this.isParentOf(event.target as HTMLElement) &&
            (this.isActiveMobileMode() || this.isActiveDesktopMode())
        ) {
            if (event.defaultPrevented) {
                return;
            }
            this.handleClickOutside(event);
        }
    }

    /**
     * Fires callback when media query changed
     * @param {MediaQueryListEvent} event
     * @private
     */
    private handleMobileMediaChanges(event: MediaQueryListEvent): void {
        const navBack = this.querySelector<HTMLElement>('.-backnav');
        if (event.matches) {
            if (this.isSearchBarOpen()) {
                this.toggleSearchBar(false);
                if (this.querySelector<UINavSearch>('ui-navsearch')) {
                    this.querySelector<UINavSearch>('ui-navsearch')!.active =
                        false;
                }
            }
            this.open = false;
            this.dropdown = false;
            this.dispatchCustomEvent('menu-toggle', {
                active: false,
            });
            this.querySelector<UINavItem>(
                'ui-navitem.-active'
            )?.classList.remove('-active');
            if (navBack) {
                navBack.tabIndex = Number(TabIndex.Active);
            }
        } else {
            document.documentElement.classList.remove('-navtoggle-expanded');
            this.updateAuxiliaryMenu(this.currentPageId());
            if (navBack) {
                navBack.tabIndex = Number(TabIndex.Inactive);
            }
        }
    }

    /**
     * Handles user clicks outside of navigation element
     * @param {Event} event
     * @private
     */
    private handleClickOutside(event: Event): void {
        if (this.isActiveDesktopMode()) {
            this.close();
        }

        if (this.isActiveMobileMode()) {
            const toggleElement = document.querySelector<UINavToggle>(
                'ui-navtoggle[for=nav-main]'
            );
            const isToggle = toggleElement?.isParentOf?.(
                event.target as HTMLElement
            );
            if (!isToggle) {
                this.dispatchCustomEvent('menu-toggle', {
                    active: false,
                });
                if (toggleElement?.firstElementChild) {
                    (toggleElement.firstElementChild as HTMLElement).focus();
                }
            }
        }
    }

    /**
     * @param {CustomEvent} event
     */
    handleOpenShortcuts(
        event: CustomEvent<DeclarativeEventDetail<UINavItem>>
    ): void {
        this.handleOpenSecondaryMenu(
            event.detail.originalEvent,
            event.detail.relatedTarget
        );
    }

    /**
     * @param {CustomEvent} event
     */
    handleEditShortcuts(event: CustomEvent<DeclarativeEventDetail>): void {
        event.detail.originalEvent.preventDefault();
        const navList =
            event.detail.relatedTarget.closest<UINavList>('ui-navlist');
        navList?.toggleEditMode?.();
    }

    /**
     * Fires callback when user clicked inside of navigation element
     * @param {Event} event
     * @private
     */
    private handleClickInside(event: Event): void {
        const eventHolder = closestEventHolder(
            event.target as HTMLElement,
            this
        );
        if (eventHolder) {
            return;
        }

        // Apparently it could be simplified with declarative events
        if (this.isActiveMobileMode()) {
            if (
                (event.target as HTMLElement).classList.contains(
                    'ui-nav__columns'
                ) ||
                (event.target as HTMLElement).parentElement?.classList.contains(
                    'ui-nav__columns'
                ) ||
                event.target === this
            ) {
                this.dispatchCustomEvent('menu-toggle', {
                    active: false,
                });
                return;
            }
        }

        const closestNavItem = (event.target as HTMLElement).closest<UINavItem>(
            'ui-navitem'
        );
        if (closestNavItem) {
            if (closestNavItem.isPrimaryItem?.()) {
                this.handleOpenSecondaryMenu(event, closestNavItem);
            }
            if (closestNavItem.isSecondaryItem?.()) {
                this.handleOpenAuxiliaryMenu(event, closestNavItem);
            }
        }
    }

    /**
     * @param {string} itemId
     * @returns {boolean}
     */
    canOpenMenu(itemId: NullOr<string>): boolean {
        return (
            !!itemId &&
            !!this.config &&
            (itemId === 'shortcuts' ||
                (this.config.menuStructure.children || []).filter(
                    (menuitem) => {
                        return (
                            itemId === menuitem.id &&
                            menuitem.children &&
                            menuitem.children.length > 0
                        );
                    }
                ).length > 0)
        );
    }

    /**
     * Opens secondary menu.
     * @param {MouseEvent | KeyboardEvent | Event | null} event
     * @param {UINavItem} navItem
     */
    handleOpenSecondaryMenu(
        event: MouseEvent | KeyboardEvent | Event | null,
        navItem: UINavItem
    ): void {
        const itemId = navItem.getAttribute('item');
        if (!this.canOpenMenu(itemId) && this.config) {
            return;
        }
        const menuId = navItem.getControlId?.();

        const menu = this.querySelector<UINavList>(
            'ui-navlist.-secondary[id="' + menuId + '"]'
        );
        if (menu) {
            if (event) {
                event.preventDefault();
            }

            if (navItem.labelBack) {
                menu.setAttribute('label-back', navItem.labelBack);
            }
            const previous =
                this.querySelector<UINavItem>('ui-navitem.-active');

            // Set previously active primary button
            this.previouslyActivePrimaryButton =
                previous || (document.activeElement as HTMLElement);

            if (previous) {
                previous.classList.remove('-active');
                previous.firstElementChild?.setAttribute(
                    'aria-expanded',
                    'false'
                );
            }
            if (previous !== navItem) {
                navItem.classList.add('-active');
                navItem.firstElementChild?.setAttribute(
                    'aria-expanded',
                    'true'
                );
                this.openSecondaryMenu(navItem, menu);
                if (this.isDesktopMode()) {
                    this.dispatchCustomEvent('menu-open', {});
                }
            } else {
                this.close();
            }
        }
    }

    /**
     * Opens auxiliary menu.
     * @param {MouseEvent | KeyboardEvent | Event | null} event
     * @param {UINavItem} navItem
     */
    handleOpenAuxiliaryMenu(
        event: MouseEvent | Event,
        navItem: UINavItem
    ): void {
        if (!navItem.expandable) {
            return;
        }

        const itemId = navItem.getAttribute('item');
        const auxiliaryMenu = this.auxiliaryColumns.querySelector<UINavList>(
            'ui-navlist[ref="' + itemId + '"]'
        );

        if (auxiliaryMenu && this.isActiveMobileMode()) {
            event.preventDefault();
            this.updateAuxiliaryMenu(itemId ?? '');
        }

        focusHandler.createFocusTrap(this.auxiliaryColumns, this.escapeHandler);
    }

    /**
     * Fires callback when menu state is updated
     * @private
     */
    private onChangeOpenState(): void {
        this.updateClassList({
            '-open': !!this.open,
        });

        if (this.isDesktopMode()) {
            return;
        }

        // Mobile
        if (this.open) {
            runInNextFrame(() =>
                document.documentElement.classList.add('-navtoggle-expanded')
            );
            this.dispatchCustomEvent('menu-open', {});
            // On primary menu open, activate focus trap on it
            focusHandler.createFocusTrap(
                this.primaryColumns,
                this.escapeHandler
            );
        } else {
            document.documentElement.classList.remove('-navtoggle-expanded');
            this.dispatchCustomEvent('menu-close', {});
            // Stop listening to document clicks when menu is not open
            this.outsideClickRemover?.();
            // Remove any focus traps when all menus are closed
            this.destroyFocusTraps();
        }
    }

    /**
     * Fires callback when environment state is updated
     * @private
     */
    private onChangeEnvironment(): void {
        if (!this.breadcrumbs) {
            return;
        }
        this.breadcrumbs.business = !!this.business;
    }

    /**
     * Fires callback when dropdown state is updated
     * @private
     */
    private onChangeDropdownState(): void {
        this.updateClassList({
            '-dropdown': !!this.dropdown,
        });
    }

    /**
     * Fires callback when menu is toggled
     * @param {CustomEvent} event
     * @private
     */
    private handleToggleMenu(event: CustomEvent<MenuToggleDetail>): void {
        this.applyToggleState(
            event.detail.for === this.id && !!event.detail.active
        );
    }

    /**
     * Applies the open/closed menu state and its side effects.
     * @param {boolean} active
     * @private
     */
    private applyToggleState(active: boolean): void {
        this.open = active;

        updateClassList(this.secondaryColumns, {
            [UINav.ModifierClasses.Next]:
                this.open && !this.isSecondaryMenuOpen(),
        });

        this.handleFocusMobileMenu(
            this.primaryColumns.querySelectorAll<UINavItem>('ui-navitem')
        );
        if (
            this.isActiveMobileMode() &&
            (this.isSecondaryMenuOpen() || this.isAuxiliaryMenuOpen())
        ) {
            this.close();
        }
    }

    /**
     * Fires callback when searchbar is toggled
     * @param {CustomEvent} event
     * @private
     */
    private handleToggleSearch(
        event: CustomEvent<MenuSearchToggleDetail>
    ): void {
        if (this.isActiveDesktopMode()) {
            this.close();
        }
        this.toggleSearchBar(!!event.detail.active);
    }

    /**
     * Fires callback when back button pressed
     * @param {Event} event
     * @private
     */
    private handleBackMenu(event: Event): void {
        const closestNavList = (event.target as HTMLElement).closest<UINavList>(
            'ui-navlist'
        );

        if (!closestNavList || !this.contains(closestNavList)) {
            return;
        }

        this.backFromList(closestNavList);
    }

    /**
     * Fires callback when popover with ui-nav was closed
     * @param {Event} event
     * @private
     */
    private handleNavtoggleClose(event: Event): void {
        if (this.layout === UINav.Layout.Popover) {
            if (this.isActiveDesktopMode()) {
                this.close();
            } else if (this.isSecondaryMenuOpen()) {
                updateClassList(this.primaryColumns, {
                    [UINav.ModifierClasses.Off]: false,
                    [UINav.ModifierClasses.On]: true,
                });

                updateClassList(this.secondaryColumns, {
                    [UINav.ModifierClasses.Off]: this.isActiveDesktopMode(),
                    [UINav.ModifierClasses.On]: false,
                    [UINav.ModifierClasses.Expanded]: false,
                    [UINav.ModifierClasses.Next]: true,
                });

                updateClassList(this.auxiliaryColumns, {
                    [UINav.ModifierClasses.Off]: false,
                    [UINav.ModifierClasses.On]: false,
                    [UINav.ModifierClasses.Next]: false,
                });
                this.closeSecondaryMenu();
                this.closest<HTMLElement>(
                    '.ui-navtoggle__content'
                )?.removeAttribute('style');
            }
        }
    }

    /**
     * Fires callback when browser history is updated
     * @param {PopStateEvent} event
     * @private
     */
    private handleBrowserHistoryState(event: PopStateEvent): void {
        const pathname = (event.target as Window).location.pathname.replace(
            '/',
            ''
        );
        this.selectMenuItem(pathname);
    }

    /**
     * Initialize menu using given config
     * @param { UINavigationConfig } config
     * @returns { UINav }
     */
    renderMenu(config: UINavigationConfig): this {
        const items = config.menuStructure.children || [];

        const primaryMenu: Array<ElementConfig> = [];
        const secondaryMenu: Array<ElementConfig> = [];
        let auxiliaryMenu: Array<ElementConfig> = [];

        if (this.useHome !== ComponentState.Off && this.homeId) {
            primaryMenu.push({
                tagName: 'ui-navitem',
                attributes: {
                    type: 'primary-icon',
                    glyph: 'redesign-house',
                    label: this.label,
                    item: this.homeId,
                    href: this.buildLink(this.homeId),
                },
            });
        }

        if (
            Array.isArray(config.shortcutsList) &&
            this.useShortcuts !== ComponentState.Off
        ) {
            primaryMenu.push({
                tagName: 'ui-navitem',
                attributes: {
                    'type': 'primary-icon',
                    'glyph': 'redesign-star',
                    'label': UINavList.labels.MY_SHORTCUTS,
                    'item': 'shortcuts',
                    'controls': 'shortcuts-nav-container',
                    'data-event': 'open-shortcuts',
                },
            });
            secondaryMenu.push(
                this.buildNavList({
                    id: 'shortcuts',
                    label: '',
                    labelENG: UINavList.labels.MY_SHORTCUTS as string,
                    visible: true,
                    children: [
                        {
                            labelENG: UINavList.labels.MY_SHORTCUTS as string,
                            label: UINavList.labels.MY_SHORTCUTS as string,
                            visible: true,
                            id: 'shortcuts',
                            children: config.shortcutsList
                                .slice(
                                    0,
                                    Math.min(
                                        config.shortcutsList.length,
                                        config.maxShortcuts!
                                    )
                                )
                                .map((item) => {
                                    item.visible = true;
                                    return item;
                                }),
                        },
                    ],
                })
            );
        }

        items.forEach((item: MenuItem) => {
            if (!item.visible) {
                return;
            }

            const menuItem: ElementConfig = {
                tagName: 'ui-navitem',
                attributes: {
                    'type': 'primary-item',
                    'label': item.label || item.labelENG,
                    'label-id': item.labelENG,
                    'item': item.id,
                    'controls': item.id + '-nav-container',
                    'href': this.buildLink(item.id),
                },
                children: [],
            };

            primaryMenu.push(menuItem);
            if (this.isPrimaryItemExpandable(item)) {
                secondaryMenu.push(this.buildNavList(item));
            }
            auxiliaryMenu = auxiliaryMenu.concat(
                this.buildAuxiliaryNavLists(item)
            );
        });

        let searchMenuBtn: UndefinedOr<ElementConfig>;

        if (this.simpleSearch) {
            searchMenuBtn = {
                tagName: 'ui-navitem',
                attributes: {
                    type: 'primary-icon',
                    glyph: 'redesign-magnifying-glass',
                    label: this.labelSearch ?? null,
                    item: 'search',
                    href: this.buildLink(this.searchHomeId!),
                },
            };
        } else if (this.useSearch !== ComponentState.Off) {
            searchMenuBtn = {
                tagName: 'ui-navsearch',
                classList: {
                    '-search': true,
                },
                attributes: {
                    'label': this.labelSearch || this.getLabel('search'),
                    'label-open':
                        this.labelSearchopen || this.getLabel('searchOpen'),
                    'label-close':
                        this.labelSearchclose || this.getLabel('searchClose'),
                },
            };
        }

        const mobileSearchMenuBtn: UndefinedOr<ElementConfig> = searchMenuBtn
            ? {
                  ...searchMenuBtn,
                  classList: {
                      ...searchMenuBtn.classList,
                      '-hide-large': true,
                  },
              }
            : undefined;

        const desktopSearchMenuBtn: UndefinedOr<ElementConfig> = searchMenuBtn
            ? {
                  ...searchMenuBtn,
                  classList: {
                      ...searchMenuBtn.classList,
                      '-show-large': true,
                  },
              }
            : undefined;

        const primaryMenuContainer =
            this.querySelector<Element>('.ui-nav__items');
        if (primaryMenuContainer !== null) {
            const customItems = queryChildren(
                primaryMenuContainer,
                'ui-navitem[type="custom"]'
            );
            primaryMenuContainer.innerHTML = '';

            const customElementsAfter = customItems.filter(
                (item: HTMLElement) => item.getAttribute('position') === 'after'
            );
            const customElementsBefore = customItems.filter(
                (item: HTMLElement) => !customElementsAfter.includes(item)
            );

            insertElements(
                primaryMenuContainer,
                [
                    ...customElementsBefore,
                    mobileSearchMenuBtn,
                    ...primaryMenu,
                    desktopSearchMenuBtn,
                    ...customElementsAfter,
                ].filter(Boolean)
            );
        }

        const secondaryMenuContainer = this.querySelector<Element>(
            '.ui-nav__columns.-secondary'
        );
        if (secondaryMenuContainer !== null) {
            secondaryMenuContainer.innerHTML = '';
            insertElements(secondaryMenuContainer, secondaryMenu);
        }

        const auxiliaryMenuContainer = this.querySelector<Element>(
            '.ui-nav__columns.-auxiliary'
        );
        if (auxiliaryMenuContainer !== null) {
            auxiliaryMenuContainer.innerHTML = '';
            insertElements(auxiliaryMenuContainer, auxiliaryMenu);
        }

        return this;
    }

    /**
     * @param {MenuItem} item
     * @returns {ElementConfig}
     */
    buildNavList(item: MenuItem): ElementConfig {
        const itemNavList: ElementConfig = {
            tagName: 'ui-navlist',
            attributes: {
                ref: item.id,
                id: item.id + '-nav-container',
            },
            children: [],
        };

        (item.children || []).forEach((subItem: MenuItem) => {
            if (!subItem.visible) {
                return;
            }

            const navListMenu: ElementConfig = {
                tagName: 'ui-navlist-menu',
                attributes: {
                    'label': subItem.label || subItem.labelENG,
                    'label-id': subItem.labelENG,
                },
                children: [],
            };

            [].forEach.call(subItem.children || [], (subSubItem: MenuItem) => {
                if (
                    !subSubItem.visible ||
                    !(subSubItem.targetPageId || subSubItem.id)
                ) {
                    return;
                }
                const subMenuItem: ElementConfig = {
                    tagName: 'ui-navitem',
                    attributes: {
                        'type': 'secondary-item',
                        'label': subSubItem.label || subSubItem.labelENG,
                        'label-id': subSubItem.labelENG,
                        'badge': subSubItem.badge || null,
                        'glyph': subSubItem.glyph ? 'new-window' : null,
                        'item': subSubItem.id,
                        'href': this.buildLink(
                            subSubItem.targetPageId || subSubItem.id
                        ),
                        'expandable':
                            this.isSecondaryItemExpandable(subSubItem) || null,
                    },
                    children: [],
                };
                (navListMenu.children as Array<ElementConfig>).push(
                    subMenuItem
                );
            });

            if (
                (navListMenu.children as Array<unknown>).length > 0 ||
                item.id === 'shortcuts'
            ) {
                (itemNavList.children as Array<ElementConfig>).push(
                    navListMenu
                );
            }
        });
        return itemNavList;
    }

    /**
     * @param {MenuItem} item
     * @returns {Array<ElementConfig>}
     */
    buildAuxiliaryNavLists(item: MenuItem): Array<ElementConfig> {
        const auxiliaryNavLists: Array<ElementConfig> = [];

        (item.children || []).forEach((subItem: MenuItem) => {
            if (!subItem.visible) {
                return;
            }

            (subItem.children || []).forEach((subSubItem: MenuItem) => {
                if (!subSubItem.visible) {
                    return;
                }

                const itemNavList: ElementConfig = {
                    tagName: 'ui-navlist',
                    attributes: {
                        type: 'auxiliary',
                        ref: subSubItem.id,
                    },
                    children: [
                        {
                            tagName: 'ui-navitem',
                            attributes: {
                                'class': '-frontpage-link',
                                'type': 'auxiliary-item',
                                'label':
                                    subSubItem.label || subSubItem.labelENG,
                                'label-id': subSubItem.labelENG,
                                'badge': subSubItem.badge || null,
                                'glyph': subSubItem.glyph ? 'new-window' : null,
                                'item': subSubItem.id,
                                'href': this.buildLink(
                                    subSubItem.targetPageId || subSubItem.id
                                ),
                            },
                            children: [],
                        },
                    ],
                };

                const auxiliaryMenuItems: ElementConfig = {
                    tagName: 'ui-columns',
                    attributes: {
                        wrap: 'wrap',
                        halign: 'left',
                    },
                    children: [],
                };

                (subSubItem.children || []).forEach(
                    (auxiliaryItem: MenuItem) => {
                        if (!auxiliaryItem.visible) {
                            return;
                        }
                        const auxiliaryMenuItem: ElementConfig = {
                            tagName: 'ui-navitem',
                            attributes: {
                                'type': 'auxiliary-item',
                                'label':
                                    auxiliaryItem.label ||
                                    auxiliaryItem.labelENG,
                                'label-id': auxiliaryItem.labelENG,
                                'badge': auxiliaryItem.badge || null,
                                'glyph': auxiliaryItem.glyph
                                    ? 'new-window'
                                    : null,
                                'item': auxiliaryItem.id,
                                'href': auxiliaryItem.href
                                    ? auxiliaryItem.href
                                    : this.buildLink(
                                          auxiliaryItem.targetPageId ||
                                              auxiliaryItem.id
                                      ),
                            },
                            children: [],
                        };
                        (
                            auxiliaryMenuItems.children as Array<ElementConfig>
                        ).push(auxiliaryMenuItem);
                    }
                );

                if (
                    (auxiliaryMenuItems.children as Array<unknown>).length > 0
                ) {
                    (itemNavList.children as Array<ElementConfig>).push(
                        auxiliaryMenuItems
                    );
                    auxiliaryNavLists.push(itemNavList);
                }
            });
        });
        return auxiliaryNavLists;
    }

    /**
     * @inheritDoc
     */
    onChangeSearchLabels(): void {
        const searchItem = this.querySelector<UINavSearch>('ui-navsearch');
        if (!searchItem) {
            return;
        }
        searchItem.updateElement?.({
            attributes: {
                'label': this.labelSearch || 'Search',
                'label-open': this.labelSearchopen || 'Open search form',
                'label-close': this.labelSearchclose || 'Close search form',
            },
        });
    }

    /**
     * Handles escape key press to close the menu.
     * @param {KeyboardEvent} event
     */
    handleEscape(event: KeyboardEvent): void {
        if (isEscapePressed(event)) {
            // Mobile
            if (this.isActiveMobileMode()) {
                // Secondary menu close
                if (this.isSecondaryMenuOpen() || this.isAuxiliaryMenuOpen()) {
                    this.handleBackMenu(event);
                    // Primary menu close
                } else {
                    this.handleClickOutside(event);
                }
            }

            // Desktop
            if (this.isActiveDesktopMode()) {
                // Focus on previously active button
                const activeMenu = this.getSelectedMenu();
                if (activeMenu) {
                    activeMenu
                        .querySelector<
                            HTMLButtonElement | HTMLLinkElement
                        >('a,button')
                        ?.focus();
                }
                // Close currently active nav menu
                this.handleClickOutside(event);
            }
        }
    }

    /**
     * @returns {HTMLElement | null}
     */
    getSelectedMenu(): NullOr<HTMLElement> {
        return this.querySelector('.-primary.-active');
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'business':
                this.onChangeEnvironment();
                break;
            case 'open':
                this.onChangeOpenState();
                break;
            case 'dropdown':
                this.onChangeDropdownState();
                break;
            case 'label':
            case 'label-search':
            case 'label-searchopen':
            case 'label-searchclose':
                this.onChangeSearchLabels();
                break;
            case 'use-breadcrumbs':
                if (
                    this.useBreadcrumbs === ComponentState.Off &&
                    this.breadcrumbs
                ) {
                    this.removeChild(this.breadcrumbs);
                }
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Removes all focus trap listeners from menus (primary, secondary, auxiliary)
     */
    destroyFocusTraps(): void {
        focusHandler.destroyFocusTrap(this.primaryColumns, this.escapeHandler);
        focusHandler.destroyFocusTrap(
            this.secondaryColumns,
            this.escapeHandler
        );
        focusHandler.destroyFocusTrap(
            this.auxiliaryColumns,
            this.escapeHandler
        );
    }

    /**
     * @private
     */
    private attachOutsideClickHandler(): void {
        this.detachOutsideClickHandler();
        const requestId = ++this.outsideClickHandlerRequestId;

        void addOutsideClickHandler(this, this.documentClicksHandler).then(
            (outsideClickRemover) => {
                if (requestId !== this.outsideClickHandlerRequestId) {
                    outsideClickRemover();
                    return;
                }

                this.outsideClickRemover = outsideClickRemover;
            }
        );
    }

    /**
     * @private
     */
    private detachOutsideClickHandler(): void {
        this.outsideClickHandlerRequestId++;
        this.outsideClickRemover?.();
        delete this.outsideClickRemover;
    }

    /**
     * Mirrors the open state from the host popover when this nav lives inside a
     * navtoggle popover. Relocation into the modal can make it miss the
     * menu-toggle event that would otherwise open it, so the state is derived
     * from whether it now sits inside a modal on mobile.
     * @private
     */
    private syncPopoverOpenState(): void {
        if (this.layout !== UINav.Layout.Popover) {
            return;
        }
        this.applyToggleState(
            this.isMobileMode() && !!this.closest('ui-modal')
        );
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        eventBus.addEventListener(
            'spa-navigation-change',
            this.navigationChangeHandler
        );
        eventBus.addEventListener('menu-toggle', this.toggleMenuHandler);
        eventBus.addEventListener(
            'menu-search-toggle',
            this.toggleSearchHandler
        );
        eventBus.addEventListener('menu-back', this.backMenuHandler);
        document.addEventListener(
            'navtoggle-closed',
            this.navtoggleCloseHandler
        );
        this.attachOutsideClickHandler();
        this.addEventListener('click', this.clickInsideHandler);
        this.mobileMediaQueryList.addEventListener(
            'change',
            this.mobileMediaChangesHandler
        );
        window.addEventListener('popstate', this.browserHistoryStateHandler);
        this.clickObserver.observe(this);
        this.addEventListener('open-shortcuts', this.openShortcutsHandler);
        this.addEventListener('edit-shortcuts', this.editShortcutsHandler);
        this.addEventListener('keydown', this.escapeHandler);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.detachOutsideClickHandler();
        this.clickObserver.disconnect();
        this.removeEventListener('click', this.clickInsideHandler);
        this.removeEventListener('open-shortcuts', this.openShortcutsHandler);
        this.removeEventListener('edit-shortcuts', this.editShortcutsHandler);
        this.removeEventListener('keydown', this.escapeHandler);
        this.mobileMediaQueryList.removeEventListener(
            'change',
            this.mobileMediaChangesHandler
        );
        window.removeEventListener('popstate', this.browserHistoryStateHandler);
        document.removeEventListener(
            'navtoggle-closed',
            this.navtoggleCloseHandler
        );
        eventBus.removeEventListener('menu-back', this.backMenuHandler);
        eventBus.removeEventListener(
            'menu-search-toggle',
            this.toggleSearchHandler
        );
        eventBus.removeEventListener('menu-toggle', this.toggleMenuHandler);
        eventBus.removeEventListener(
            'spa-navigation-change',
            this.navigationChangeHandler
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('role', 'navigation');
        const items = this.queryChildren('ui-navitem');
        const menus = this.queryChildren('ui-navlist');
        const wrapper = this.wrapper;
        const breadcrumbs =
            this.useBreadcrumbs === ComponentState.Off
                ? null
                : this.createElement({
                      tagName: 'ui-breadcrumbs',
                      element: this.querySelector('ui-breadcrumbs'),
                      attributes: {
                          business: this.business ? 'business' : null,
                      },
                  });

        this.updateClassList({
            '-main': true,
            '-columns': true,
            '-dropdown': false,
        });

        this.insertAdjacentElement(
            'afterbegin',
            this.createElement({
                tagName: 'div',
                ...(wrapper ? { element: wrapper } : {}),
                attributes: {
                    class: 'ui-nav__columns-wrapper',
                },
                children: [
                    {
                        tagName: 'div',
                        classList: {
                            'ui-nav__columns': true,
                            [UINav.ModifierClasses.Primary]: true,
                            [UINav.ModifierClasses.On]: true,
                        },
                        children: [
                            {
                                tagName: 'div',
                                classList: {
                                    'ui-nav__items': true,
                                },
                                attributes: {
                                    role: 'list',
                                },
                                children: items,
                            },
                        ],
                    },
                    {
                        tagName: 'div',
                        classList: {
                            'ui-nav__columns': true,
                            '-secondary': true,
                        },
                        children: menus,
                    },
                    {
                        tagName: 'div',
                        classList: {
                            'ui-nav__columns': true,
                            '-auxiliary': true,
                        },
                        children: this.getChildrenForSlot('menu-auxiliary'),
                    },
                ],
            })
        );

        if (breadcrumbs) {
            this.appendChild(breadcrumbs);
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
        this.destroyFocusTraps();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
        this.syncPopoverOpenState();
    }
}
